import { AddProfileCard } from '@/components/billing/AddProfileCard';
import { BillingSummaryCard } from '@/components/billing/BillingSummaryCard';
import { CloseGymCard } from '@/components/billing/CloseGymCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CreditCard,
  ExternalLink,
  Loader2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig: Record<
  string,
  { color: string; icon: typeof CheckCircle; label: string; description: string }
> = {
  active: {
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    icon: CheckCircle,
    label: 'Active',
    description: 'Your subscription is active and in good standing.',
  },
  pending: {
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    icon: AlertCircle,
    label: 'Pending',
    description: 'Complete your affiliate application to continue to payment.',
  },
  past_due: {
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    icon: AlertCircle,
    label: 'Past Due',
    description: 'Your payment is past due. Please update your payment method.',
  },
  cancelled: {
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
    icon: XCircle,
    label: 'Cancelled',
    description: 'Your subscription has been cancelled. Resubscribe to continue access.',
  },
};

export default function AffiliateBilling() {
  const affiliatesApi = useAffiliatesApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['affiliate-dashboard'],
    queryFn: async () => {
      const response = await affiliatesApi.getDashboard();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      const response = await affiliatesApi.getBillingPortal();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to open billing portal');
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const response = await affiliatesApi.createCheckoutSession();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    },
  });

  const handleManageBilling = () => {
    billingPortalMutation.mutate();
  };

  const handleSubscribe = () => {
    if (data?.affiliate?.status === 'pending') {
      window.location.href = '/apply/affiliate';
      return;
    }
    checkoutMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load billing information</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  const status = data?.affiliate?.status || 'pending';
  const isPendingApproval = data?.billing_state === 'pending_approval';
  const showSubscribePrompt =
    !isPendingApproval && (status === 'pending' || status === 'cancelled');
  const defaultConfig = {
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    icon: AlertCircle,
    label: 'Pending',
    description: 'Complete your affiliate application to continue to payment.',
  };
  const pendingApprovalConfig = {
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    icon: CheckCircle,
    label: 'Awaiting Approval',
    description: 'Your payment was received. HQ is reviewing your affiliate application.',
  };
  const config = isPendingApproval
    ? pendingApprovalConfig
    : (statusConfig[status] ?? defaultConfig);
  const StatusIcon = config?.icon ?? CheckCircle;
  const configColor = config?.color ?? defaultConfig.color;
  const configLabel = config?.label ?? defaultConfig.label;
  const configDescription = config?.description ?? defaultConfig.description;

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Billing</h1>
        <p className="mt-1 text-muted-foreground">Manage your subscription and payment details.</p>
      </div>

      {isPendingApproval && (
        <Card className="mb-6 border-green-500/30 bg-green-500/5 animate-fade-in">
          <CardContent className="flex items-start gap-4 pt-6">
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-500">
                Payment received — awaiting HQ approval
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                No further payment is required. We’ll notify you when your affiliate application has
                been reviewed.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {showSubscribePrompt && (
        <Card className="mb-6 border-yellow-500/30 bg-yellow-500/5 animate-fade-in">
          <CardContent className="flex items-start gap-4 pt-6">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-500">
                {status === 'cancelled' ? 'Subscription Cancelled' : 'Application Incomplete'}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {status === 'cancelled'
                  ? 'Your previous subscription has ended. Resubscribe to restore gym access.'
                  : 'Complete your affiliate application to continue to secure payment.'}
              </p>
              <Button
                className="mt-3"
                onClick={handleSubscribe}
                disabled={checkoutMutation.isPending}
              >
                {checkoutMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                {status === 'cancelled' ? 'Resubscribe' : 'Complete application'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {status === 'past_due' && (
        <Card className="mb-6 border-orange-500/30 bg-orange-500/5 animate-fade-in">
          <CardContent className="flex items-start gap-4 pt-6">
            <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-orange-500">Payment Required</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your latest payment was unsuccessful. Update your payment method to restore full
                access.
              </p>
              <Button
                className="mt-3"
                onClick={handleManageBilling}
                disabled={billingPortalMutation.isPending}
              >
                {billingPortalMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Update Payment Method
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Subscription Status */}
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Subscription Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-4">
              <div
                className={`rounded-full p-3 ${
                  status === 'active'
                    ? 'bg-green-500/10'
                    : status === 'cancelled'
                      ? 'bg-red-500/10'
                      : 'bg-yellow-500/10'
                }`}
              >
                <StatusIcon
                  className={`h-6 w-6 ${
                    status === 'active'
                      ? 'text-green-500'
                      : status === 'cancelled'
                        ? 'text-red-500'
                        : 'text-yellow-500'
                  }`}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={configColor}>
                    {configLabel}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{configDescription}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t border-border">
              {isPendingApproval ? (
                <p className="text-sm text-center text-muted-foreground">
                  No further payment is required while approval is pending.
                </p>
              ) : status === 'active' || status === 'past_due' ? (
                <Button
                  onClick={handleManageBilling}
                  disabled={billingPortalMutation.isPending}
                  className="w-full"
                >
                  {billingPortalMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Manage Subscription
                </Button>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  disabled={checkoutMutation.isPending}
                  className="w-full"
                >
                  {checkoutMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {status === 'cancelled' ? 'Resubscribe' : 'Complete application'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Plan Details */}
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardHeader>
            <CardTitle>ErgShoot Affiliate Plan</CardTitle>
            <CardDescription>Full access to ErgShoot programming and features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-3xl font-bold text-primary">
              £67.15<span className="text-lg text-muted-foreground font-normal">/month</span>
            </div>

            <ul className="space-y-3">
              {[
                'Access to all ErgShoot programming',
                'Unlimited member accounts',
                'TV display mode for your gym',
                'Leaderboard and tracking',
                'Event management',
                'Priority support',
              ].map((feature, index) => (
                <li
                  key={feature}
                  className="flex items-center gap-2 text-sm animate-fade-in"
                  style={{ animationDelay: `${100 + index * 30}ms` }}
                >
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            {status !== 'active' && !isPendingApproval && (
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Cancel anytime. No long-term commitment required.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* STORY-STRIPE-002 — In-App Billing Summary */}
        <div className="lg:col-span-2">
          <BillingSummaryCard scope="affiliate" affiliateId={data?.affiliate?.id} />
        </div>

        {/* STORY-STRIPE-009a — life-stage upgrade: owner → also individual */}
        <div className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '50ms' }}>
          <AddProfileCard variant="add_individual" />
        </div>

        {/* STORY-STRIPE-009c — gym closure (destructive, guarded) */}
        {data?.affiliate?.id && data.affiliate.name && status !== 'cancelled' && (
          <div className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '75ms' }}>
            <CloseGymCard affiliateId={data.affiliate.id} affiliateName={data.affiliate.name} />
          </div>
        )}

        {/* Help Card */}
        <Card className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <CardTitle className="text-base">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you have questions about billing or need to update your payment information, you
              can manage everything through the Stripe billing portal. For other inquiries, contact
              us at{' '}
              <a href="mailto:support@ergshoot.com" className="text-primary hover:underline">
                support@ergshoot.com
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
